import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, Container, H1, Input, Loader, Modal, Table } from '@itds/elements/index.js';
import DOMPurify from 'dompurify';
import { Api } from '@carrier/utils/Api';

import NewDrawer from './New';
import {
  fetchCarriers,
  clearCache,
  setSearchQuery,
  setCurrentPage
} from '@redux/carrierSlices/carriersSlice';
import { useMemo } from 'react';
import { debounce } from 'lodash';

const List = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { carriers, pagination, loading, error, currentPage, searchQuery } = useSelector(
    state => state.carriers
  );

  const [modal, setModal] = useState(null);
  const [carrier, setCarrier] = useState(null);
  const [showNewCarrier, toggleNewCarrier] = useState(false);
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    dispatch(
      fetchCarriers({
        page: currentPage,
        search: searchQuery
      })
    );
  }, [currentPage, searchQuery, dispatch]);

  const handleDelete = (carrier, e) => {
    e.stopPropagation();
    setModal('delete');
    setCarrier(carrier);
  };

  const confirmDelete = async () => {
    try {
      window.scrollTo(0, 0);
      await Api.deleteCarrier(carrier.id);

      setModal(null);
      setCarrier(null);
      dispatch(clearCache());

      dispatch(
        fetchCarriers({
          page: currentPage,
          search: searchQuery,
          forceRefresh: true
        })
      );

      setSuccessMessage('Carrier deleted successfully');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (error) {
      setModal(null);
      setCarrier(null);
      setSuccessMessage('Unable to delete carrier at this time');
      setTimeout(() => setSuccessMessage(''), 2000);
    }
  };

  const handlePageChange = newPage => {
    window.scrollTo(0, 0);
    dispatch(setCurrentPage(newPage));
  };

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce(value => {
        dispatch(setSearchQuery(value));
      }, 500),
    [dispatch]
  );
  const handleSearchChange = e => {
    let value = e.target.value.trimStart();

    value = DOMPurify.sanitize(value);

    setLocalSearch(value);

    debouncedSetSearchTerm(value);
  };

  const performSearch = e => {
    if (e.key === 'Enter') {
      dispatch(setCurrentPage(1));
      dispatch(clearCache());
    }
  };

  if (loading && !carriers.length) {
    return <Loader />;
  }

  return (
    <Container>
      <NewDrawer
        onClose={() => toggleNewCarrier(false)}
        onSave={() => {
          toggleNewCarrier(false);
          dispatch(clearCache());
          dispatch(
            fetchCarriers({
              page: currentPage,
              search: searchQuery,
              forceRefresh: true
            })
          );
        }}
        show={showNewCarrier}
      />

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <H1>Carriers</H1>
      </div>

      <div style={{ display: 'flex', marginBottom: 30 }}>
        <Input
          onChange={handleSearchChange}
          onKeyDown={performSearch}
          placeholder='Search by carrier name...'
          style={{ width: 300 }}
          value={localSearch}
        />

        <Button onClick={() => toggleNewCarrier(true)} style={{ marginLeft: 'auto' }}>
          Add Carrier
        </Button>
      </div>

      {error && <Alert type='error'>{error}</Alert>}
      {successMessage && <Alert type='success'>{successMessage}</Alert>}

      <Table
        apiPagination={{
          ...pagination,
          current_page: currentPage,
          onClick: handlePageChange
        }}
        columns={[
          {
            id: 'name',
            label: 'Name'
          },
          {
            id: 'id',
            label: 'ID'
          },
          {
            id: 'actions',
            styles: { textAlign: 'right' },
            render: carrier => (
              <Button
                color='RED'
                inline={true}
                onClick={e => handleDelete(carrier, e)}
                small={true}
              >
                Delete
              </Button>
            )
          }
        ]}
        data={carriers}
        defaultSortColumn={{ id: 'name' }}
        onRowClick={carrier => navigate(`${carrier.id}`)}
      />

      {modal === 'delete' && (
        <Modal
          buttons={[
            {
              color: 'RED',
              children: 'Confirm Delete',
              onClick: confirmDelete,
              style: { marginLeft: 'auto' }
            }
          ]}
          contained={true}
          onClose={() => setModal(null)}
          title={`Delete "${carrier?.name}"`}
        >
          <div style={{ padding: 40, fontSize: 16, textAlign: 'center', lineHeight: '1.5em' }}>
            Are you sure you want to delete carrier: "{carrier?.name}"?
          </div>
        </Modal>
      )}
    </Container>
  );
};

export default List;
